import { t } from '@lingui/macro'
import { DataQualityGrade } from '@lune-climate/lune'
import { LuneTheme, Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'

const { palette } = LuneTheme
export const getDataQualityScoreColor = (letter: DataQualityGrade): string => {
    switch (letter) {
        case 'A':
        case 'A_plus':
        case 'A_minus':
            return palette.dataColors.DataQualityScoreA
        case 'B':
        case 'B_plus':
        case 'B_minus':
            return palette.dataColors.DataQualityScoreB
        case 'C':
        case 'C_plus':
        case 'C_minus':
            return palette.dataColors.DataQualityScoreC
        case 'D':
            return palette.dataColors.DataQualityScoreD
        default:
            return 'white'
    }
}

export const getDataQualityScoreLabel = (letter: DataQualityGrade): string => {
    switch (letter) {
        case 'A':
        case 'A_plus':
        case 'A_minus':
            return t`Excellent`
        case 'B':
        case 'B_plus':
        case 'B_minus':
            return t`Good`
        case 'C':
        case 'C_plus':
        case 'C_minus':
            return t`Fair`
        default:
            return t`Poor`
    }
}

export const getDataQualityScoreFormattedLetter = (letter: DataQualityGrade): string => {
    switch (letter) {
        case 'A_plus':
            return 'A+'
        case 'A_minus':
            return 'A-'
        case 'B_plus':
            return 'B+'
        case 'B_minus':
            return 'B-'
        case 'C_plus':
            return 'C+'
        case 'C_minus':
            return 'C-'
        default:
            return letter
    }
}

export const getDataQualityScorePercentage = (letter: DataQualityGrade): number => {
    switch (letter) {
        case 'A_plus':
            return 100
        case 'A':
            return 90
        case 'A_minus':
            return 80
        case 'B_plus':
            return 70
        case 'B':
            return 60
        case 'B_minus':
            return 50
        case 'C_plus':
            return 40
        case 'C':
            return 30
        case 'C_minus':
            return 20
        default:
            return 10
    }
}

export const DataQualityScoreCircle = ({
    score,
    size,
    empty,
}: {
    score: DataQualityGrade
    size?: 'md' | 'lg'
    empty?: boolean
}) => {
    const dimensions = !size ? 48 : size === 'md' ? 56 : 104
    const cYX = !size ? 24 : size === 'md' ? 28 : 52
    const strokeWidth = !size ? 6 : size === 'md' ? 6 : 10
    const r = cYX - strokeWidth / 2
    const perimeter = 2 * Math.PI * r
    const strokeDashoffset = perimeter - (getDataQualityScorePercentage(score) * perimeter) / 100

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-block',
                width: `${dimensions}px`,
                height: `${dimensions}px`,
                svg: {
                    transform: 'rotate(-90deg)',
                },
                '.circle_animation': {
                    strokeDasharray: 100,
                    strokeDashoffset,
                },
            }}
        >
            <Text
                variant={size === 'lg' ? 'h4' : 'h6'}
                style={{
                    textAlign: 'center',
                    position: 'absolute',
                    lineHeight: `${dimensions}px`,
                    width: '100%',
                }}
            >
                {empty ? '-' : getDataQualityScoreFormattedLetter(score)}
            </Text>
            <svg
                width={dimensions}
                height={dimensions}
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: 'rotate(-90deg)' }}
            >
                <g>
                    <circle
                        r={r}
                        cy={cYX}
                        cx={cYX}
                        strokeWidth={strokeWidth}
                        stroke="#eaeaea"
                        fill="none"
                    />
                    {!empty && (
                        <circle
                            className="circle_animation"
                            r={r}
                            cy={cYX}
                            cx={cYX}
                            strokeWidth={strokeWidth}
                            stroke={getDataQualityScoreColor(score)}
                            fill="none"
                            strokeLinecap={'round'}
                            style={{
                                strokeDasharray: perimeter,
                                strokeDashoffset,
                            }}
                        />
                    )}
                </g>
            </svg>
        </Box>
    )
}

export default DataQualityScoreCircle
